import { useNavigate } from 'react-router-dom'
import { Package } from '@phosphor-icons/react'
import { DataTable, EmptyState, type Column } from '@/ui'
import { errorMessage } from '../../api/core'
import { usePackLots, useSkuOptions, type PackLotRow } from '../../api/lab'
import { DateFilter, FilterBar, SearchFilter, SelectFilter } from '../../components/FilterBar'
import { ConsolePage } from '../../components/Layout'
import { Check, Code, Kg, When } from '../../components/bits'
import { PackLotState } from '../../components/lab/PackLotState'
import { useUrlState } from '../../lib/useUrlState'
import { PACK_LOT_HOLD_LABEL, PACK_LOT_STATUSES, PACK_LOT_STATUS_LABEL } from '../../model/lab/labText'

const LIMIT = 25

/** Pack lots (F7.4): every lot of pouches, its counts, release state and hold. */
export function PackLotsPage() {
  const navigate = useNavigate()
  const [f, setF, { reset, active }] = useUrlState({ search: '', status: '', hold: '', skuId: '', packedFrom: '', packedTo: '', page: 1 })
  const skus = useSkuOptions()
  const q = usePackLots({
    page: f.page,
    limit: LIMIT,
    search: f.search || undefined,
    status: f.status || undefined,
    hold: f.hold || undefined,
    skuId: f.skuId || undefined,
    packedFrom: f.packedFrom || undefined,
    packedTo: f.packedTo || undefined,
  })

  const columns: Column<PackLotRow>[] = [
    { id: 'code', header: 'Pack lot', cell: (r) => <Code className="whitespace-nowrap">{r.code}</Code> },
    {
      id: 'sku',
      header: 'Product',
      cell: (r) => (
        <span className="flex flex-col leading-tight">
          <span>{r.skuName ?? '—'}</span>
          {r.skuCode && <span className="font-mono text-[11.5px] text-muted">{r.skuCode}</span>}
        </span>
      ),
    },
    { id: 'packed', header: 'Packed', cell: (r) => <When at={r.packDate} dateOnly />, hideBelow: 'md' },
    { id: 'bb', header: 'Best before', cell: (r) => <When at={r.bestBefore} dateOnly />, hideBelow: 'lg' },
    { id: 'pouches', header: 'Pouches', align: 'right', mono: true, cell: (r) => r.pouchCount ?? '—' },
    {
      id: 'left',
      header: 'Not dispatched',
      align: 'right',
      mono: true,
      cell: (r) => (r.pouchCount === null ? '—' : r.pouchCount - r.pouchesDispatched),
      hideBelow: 'md',
    },
    { id: 'kg', header: 'Packed kg', align: 'right', cell: (r) => <Kg grams={r.packedG} />, hideBelow: 'lg' },
    {
      id: 'labels',
      header: 'Labels add up',
      cell: (r) => {
        const all = [r.labelsPrinted, r.labelsApplied, r.labelsSpoiled, r.labelsDestroyed]
        if (all.some((x) => x === null)) return <Check ok={null} label={<span className="text-muted">Not closed</span>} />
        const ok = r.labelsPrinted === r.labelsApplied! + r.labelsSpoiled! + r.labelsDestroyed! && r.pouchCount === r.labelsApplied
        return <Check ok={ok} label={ok ? 'Yes' : 'No'} />
      },
      hideBelow: 'md',
    },
    { id: 'state', header: 'Status', cell: (r) => <PackLotState status={r.status} hold={r.hold} short /> },
  ]

  return (
    <ConsolePage
      eyebrow="Pack & lab"
      title="Pack lots"
      description="Every lot of pouches from the pack line: counts, label reconciliation, release and hold."
    >
      <DataTable<PackLotRow>
        caption="Pack lots"
        columns={columns}
        rows={q.data?.rows ?? []}
        getRowId={(r) => r.id}
        loading={q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        onRowClick={(r) => navigate(`/c/pack-lots/${r.id}`)}
        pageSize={LIMIT}
        server={{ page: f.page, total: q.data?.total ?? 0, onPageChange: (page) => setF({ page }) }}
        filters={
          <FilterBar onClear={reset} active={active}>
            <SearchFilter label="Pack lot code" value={f.search} onChange={(search) => setF({ search })} placeholder="e.g. PL-2026-1010" />
            <SelectFilter
              label="Status"
              value={f.status}
              onChange={(status) => setF({ status })}
              options={PACK_LOT_STATUSES.map((s) => ({ value: s, label: PACK_LOT_STATUS_LABEL[s] ?? s }))}
            />
            <SelectFilter
              label="Hold"
              value={f.hold}
              onChange={(hold) => setF({ hold })}
              options={(['NONE', 'HELD', 'RECALLED'] as const).map((h) => ({ value: h, label: PACK_LOT_HOLD_LABEL[h] ?? h }))}
            />
            <SelectFilter
              label="Product"
              value={f.skuId}
              onChange={(skuId) => setF({ skuId })}
              options={(skus.data ?? []).map((s) => ({ value: s.id, label: `${s.name} · ${s.code}` }))}
            />
            <DateFilter label="Packed from" value={f.packedFrom} onChange={(packedFrom) => setF({ packedFrom })} />
            <DateFilter label="Packed to" value={f.packedTo} onChange={(packedTo) => setF({ packedTo })} />
          </FilterBar>
        }
        empty={<EmptyState icon={<Package />} title={active ? 'No pack lots match' : 'No pack lots yet'} description="A pack lot appears when a pack run starts on the line." />}
      />
    </ConsolePage>
  )
}
